import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Users, Loader2, Wrench, Car, Edit, ToggleLeft, ToggleRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  atelier: { label: "Atelier", cls: "bg-blue-500/20 text-blue-300 border-blue-500/30", icon: Wrench },
  "depot-vente": { label: "Dépôt-Vente", cls: "bg-purple-500/20 text-purple-300 border-purple-500/30", icon: Car },
  both: { label: "Les deux", cls: "bg-[#FF3B30]/20 text-[#FF3B30] border-[#FF3B30]/30", icon: Users },
};

const RolePill = ({ role }) => {
  const meta = ROLE_LABELS[role] || ROLE_LABELS.atelier;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded border ${meta.cls}`}>
      <Icon size={11} />
      {meta.label}
    </span>
  );
};

const EMPTY_FORM = { name: "", role: "atelier", active: true };

export default function TechniciensTab() {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null | "new" | tech.id
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchTechnicians = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/technicians`);
      setTechnicians(res.data.technicians || []);
    } catch {
      toast.error("Erreur au chargement des techniciens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTechnicians();
  }, [fetchTechnicians]);

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId("new");
  };

  const startEdit = (tech) => {
    setForm({ name: tech.name, role: tech.role || "atelier", active: tech.active !== false });
    setEditingId(tech.id);
  };

  const cancelEdit = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const saveTechnician = async () => {
    if (!form.name.trim()) {
      toast.error("Le nom est obligatoire");
      return;
    }
    setSaving(true);
    try {
      if (editingId === "new") {
        await axios.post(`${API}/api/admin/technicians`, form);
        toast.success("Technicien ajouté");
      } else {
        await axios.put(`${API}/api/admin/technicians/${editingId}`, form);
        toast.success("Technicien mis à jour");
      }
      await fetchTechnicians();
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const deleteTechnician = async (tech) => {
    if (!window.confirm(`Supprimer définitivement "${tech.name}" ?\n\nLes rendez-vous existants déjà assignés à ${tech.name} ne seront PAS impactés.`)) return;
    try {
      await axios.delete(`${API}/api/admin/technicians/${tech.id}`);
      toast.success("Technicien supprimé");
      await fetchTechnicians();
    } catch {
      toast.error("Erreur à la suppression");
    }
  };

  const toggleActive = async (tech) => {
    try {
      await axios.put(`${API}/api/admin/technicians/${tech.id}`, { active: !tech.active });
      toast.success(tech.active ? `${tech.name} désactivé` : `${tech.name} activé`);
      await fetchTechnicians();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tech-tab">
      <div className="bg-[#121212] border border-white/[0.06] rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">
              <Users size={16} className="text-[#FF3B30]" />
              Techniciens ({technicians.length})
            </h3>
            <p className="text-white/50 text-xs mt-1">
              Gère la liste de tes techniciens — utilisée dans les assignations de rendez-vous et pour identifier qui a déposé un véhicule sur le dépôt-vente. Les techniciens <strong>inactifs</strong> n&apos;apparaissent plus dans les dropdowns mais leurs données historiques restent.
            </p>
          </div>
          {editingId ? null : (
            <button
              onClick={startNew}
              data-testid="admin-tech-add-btn"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-4 py-2.5 rounded-md text-sm uppercase tracking-wider"
            >
              <Plus size={14} />
              Ajouter
            </button>
          )}
        </div>

        {editingId ? (
          <TechnicianForm
            form={form}
            setForm={setForm}
            onSave={saveTechnician}
            onCancel={cancelEdit}
            saving={saving}
            isNew={editingId === "new"}
          />
        ) : loading ? (
          <div className="text-center py-10 text-white/40">
            <Loader2 size={20} className="animate-spin mx-auto mb-2" />
            Chargement...
          </div>
        ) : technicians.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-white/[0.06] rounded-lg">
            <Users size={32} className="text-white/20 mx-auto mb-2" />
            <p className="text-white/60 text-sm">Aucun technicien</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {technicians.map((tech) => (
              <article
                key={tech.id}
                data-testid={`admin-tech-${tech.id}`}
                className={`bg-[#0A0A0A] border rounded-md p-4 flex items-center gap-3 transition-colors ${
                  tech.active === false ? "border-white/[0.04] opacity-60" : "border-white/[0.06] hover:border-[#FF3B30]/30"
                }`}
              >
                <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg ${
                  tech.active === false ? "bg-white/[0.04] text-white/30" : "bg-[#FF3B30]/[0.15] text-[#FF3B30]"
                }`}>
                  {tech.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className={`font-bold truncate ${tech.active === false ? "text-white/40" : "text-white"}`}>
                      {tech.name}
                    </h4>
                    <RolePill role={tech.role} />
                  </div>
                  <div className="text-white/50 text-xs">
                    {tech.active === false ? (
                      <span className="text-white/40">Inactif</span>
                    ) : (
                      <span className="text-green-400">Actif</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggleActive(tech)}
                    data-testid={`admin-tech-toggle-${tech.id}`}
                    className="text-white/60 hover:text-white p-2 rounded-md hover:bg-white/[0.06]"
                    title={tech.active === false ? "Activer" : "Désactiver"}
                  >
                    {tech.active === false ? <ToggleLeft size={16} /> : <ToggleRight size={16} className="text-green-400" />}
                  </button>
                  <button
                    onClick={() => startEdit(tech)}
                    data-testid={`admin-tech-edit-${tech.id}`}
                    className="text-white/60 hover:text-white p-2 rounded-md hover:bg-white/[0.06]"
                    title="Modifier"
                  >
                    <Edit size={14} />
                  </button>
                  <button
                    onClick={() => deleteTechnician(tech)}
                    data-testid={`admin-tech-delete-${tech.id}`}
                    className="text-white/60 hover:text-red-400 p-2 rounded-md hover:bg-red-500/[0.06]"
                    title="Supprimer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TechnicianForm = ({ form, setForm, onSave, onCancel, saving, isNew }) => {
  const upd = (patch) => setForm({ ...form, ...patch });
  return (
    <div className="bg-[#0A0A0A] border border-[#FF3B30]/30 rounded-md p-5" data-testid="admin-tech-form">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-white font-bold">{isNew ? "Ajouter un technicien" : "Modifier le technicien"}</h4>
        <button onClick={onCancel} className="text-white/50 hover:text-white p-1"><X size={16} /></button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Nom *</label>
          <input
            type="text"
            value={form.name}
            data-testid="admin-tech-form-name"
            onChange={(e) => upd({ name: e.target.value })}
            className="w-full bg-[#121212] border border-white/[0.08] rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3B30]"
            placeholder="Ex : Amine"
          />
        </div>

        <div>
          <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Rôle</label>
          <select
            value={form.role}
            data-testid="admin-tech-form-role"
            onChange={(e) => upd({ role: e.target.value })}
            className="w-full bg-[#121212] border border-white/[0.08] rounded-md px-3 py-2.5 text-white"
          >
            <option value="atelier">Atelier — apparaît dans l&apos;assignation des rendez-vous</option>
            <option value="depot-vente">Dépôt-Vente — travaille sur les véhicules d&apos;occasion</option>
            <option value="both">Les deux — atelier + dépôt-vente</option>
          </select>
        </div>

        <label className="flex items-center gap-2 cursor-pointer bg-[#121212] border border-white/[0.06] rounded-md px-4 py-3">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => upd({ active: e.target.checked })}
            data-testid="admin-tech-form-active"
            className="w-4 h-4 accent-[#FF3B30]"
          />
          <span className="text-white/80 text-sm">Actif (apparaît dans les dropdowns de sélection)</span>
        </label>

        <div className="flex gap-3 pt-3 border-t border-white/[0.06]">
          <button
            onClick={onSave}
            disabled={saving || !form.name.trim()}
            data-testid="admin-tech-form-save"
            className="flex-1 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold py-2.5 rounded-md uppercase tracking-wider text-sm flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Enregistrement..." : isNew ? "Ajouter" : "Enregistrer"}
          </button>
          <button
            onClick={onCancel}
            className="px-6 bg-white/[0.06] hover:bg-white/[0.12] text-white/80 font-medium rounded-md text-sm"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};
